/**
 * fighterDroneConstants.ts
 * Tuning knobs for the World 2 `FighterDrone` — a linear left-drifting
 * fighter with 1 HP. Split out of `spaceEnemyConstants.ts` so each
 * enemy kind owns its own constants file (Phase 5 Tech Debt — file
 * length cap split, see ROADMAP.md).
 *
 * Consumers should keep importing from `spaceEnemyConstants` — that
 * module re-exports every symbol here so the public surface is
 * unchanged.
 *
 * @module fighterDroneConstants
 */

/** Hitbox width of a fighter drone in logical px (tighter than the 48 px sprite). */
export const FIGHTER_DRONE_WIDTH = 36;

/** Hitbox height of a fighter drone in logical px (tighter than the 48 px sprite). */
export const FIGHTER_DRONE_HEIGHT = 28;

/** Starting hit points. One ship-projectile hit kills a fighter drone. */
export const FIGHTER_DRONE_HP = 1;

/**
 * Leftward drift relative to world space (px / s).
 * On top of the auto-scrolling camera (80 px/s) this gives a 200 px/s apparent
 * on-screen velocity — fast enough to feel like an oncoming hostile, slow
 * enough that the player has time to react and shoot.
 */
export const FIGHTER_DRONE_DRIFT_SPEED = 120;

/**
 * Vertical sine wave amplitude (px). The drone bobs up and down by
 * this many pixels around its spawn Y as it drifts left.
 *
 * DECISION (W2 overhaul): Added so the player can't camp a single
 * vertical lane and finish a level untouched. The old linear path
 * was trivially avoidable.
 */
export const FIGHTER_DRONE_WAVE_AMPLITUDE_PX = 48;

/**
 * Vertical sine wave frequency (Hz). One full bob cycle every
 * `1 / FIGHTER_DRONE_WAVE_FREQUENCY_HZ` seconds.
 */
export const FIGHTER_DRONE_WAVE_FREQUENCY_HZ = 0.6;

// ── Fighter drone laser (secondary fire) ─────────────────────────────

/** Hitbox width of a fighter-drone laser bolt (logical px). */
export const FIGHTER_DRONE_LASER_WIDTH = 10;

/** Hitbox height of a fighter-drone laser bolt (logical px). */
export const FIGHTER_DRONE_LASER_HEIGHT = 4;

/**
 * Travel speed of a fighter-drone laser bolt (px / s). Slower than the
 * ship's 540 px/s outgoing pulse so the player can read and dodge the
 * incoming threat; combined with the 80 px/s auto-scroll, apparent
 * relative speed is ~400 px/s — about a viewport-width per second.
 */
export const FIGHTER_DRONE_LASER_SPEED = 320;

/**
 * Mean seconds between fire attempts per drone. Each drone runs its
 * own countdown so a wave of fighters doesn't sync-fire; the spawn
 * helper seeds the timer with a per-drone phase offset. Pitched higher
 * than the boss turret cadence because multiple fighters can be
 * on-screen simultaneously and the cumulative bolt density needs to
 * stay readable.
 */
export const FIGHTER_DRONE_FIRE_INTERVAL_S = 1.8;

/**
 * Per-drone delay before the FIRST shot is offered (seconds). Keeps a
 * freshly-spawned drone from firing while it is still in the spawn
 * margin past the right viewport edge — by the time the delay expires
 * the drone has drifted into view and its bolt is gameplay-readable.
 */
export const FIGHTER_DRONE_FIRE_INITIAL_DELAY_S = 0.6;

/**
 * Off-screen margin (px) added to the LEFT edge of the visible window
 * before a fighter-drone laser is culled. Mirrors `BossProjectile`'s
 * margin so the cull behaviour is symmetric across enemy bolt streams.
 */
export const FIGHTER_DRONE_LASER_OFFSCREEN_MARGIN = 32;

/**
 * Pool cap for in-flight fighter-drone lasers. AGENTS.md caps all
 * pools at 50. The ceiling here is governed by:
 *   - `SPACE_ENEMY_MAX_ACTIVE = 12` simultaneous enemies, and
 *   - laser travel time across the viewport ≈ 1.2 s (at 400 px/s
 *     apparent), so at the 1.8 s cadence at most ~2 bolts per
 *     drone live in the viewport at once.
 * 24 leaves ~2× headroom and is well under the AGENTS.md ceiling.
 */
export const FIGHTER_DRONE_LASER_POOL_CAP = 24;
